/**
 * MONSTER TOWN — THE BOOK'S OWN FURNITURE. FISHING/BUGS CREW (src/catching/**).
 *
 * Everything on a book screen that is not a creature: the button that opens it,
 * the four page tabs, the four skies that say WHEN something is out and the
 * four places that say WHERE, and the friendly egg that stands in for a
 * creature nobody has found yet.
 *
 * All of it is a picture, because none of it is allowed to be a word (LAW #4).
 * Split out of `art2d.ts` when that file went past the repo's 500-line rule.
 */

import { css, type PaletteName } from '../core/palette'
import { dot, ellipse, fillStroke, INK, inBox, line, type Painter } from './paint'

// ---------------------------------------------------------------------------
// book chrome — the net, the empty page, the two page tabs
// ---------------------------------------------------------------------------

/** The button that opens the book, and the tab for bugs. */
export const netIcon: Painter = (c, size) =>
  inBox(c, size, () => {
    line(
      c,
      [
        [16, 92],
        [46, 58],
      ],
      10,
    )
    ellipse(c, 62, 40, 32, 30, -0.5)
    c.fillStyle = css('uiPanel')
    c.fill()
    c.save()
    c.clip()
    c.strokeStyle = css('inkSoft')
    c.lineWidth = 2.5
    for (let i = -3; i <= 3; i++) {
      c.beginPath()
      c.moveTo(62 + i * 12, 4)
      c.lineTo(62 + i * 12, 78)
      c.stroke()
      c.beginPath()
      c.moveTo(24, 40 + i * 11)
      c.lineTo(100, 40 + i * 11)
      c.stroke()
    }
    c.restore()
    ellipse(c, 62, 40, 32, 30, -0.5)
    c.lineWidth = 7
    c.strokeStyle = INK
    c.stroke()
  })

/**
 * ============ THE BUTTON THAT OPENS THE BOOK IS A BOOK (fixed vr1) ============
 *
 * It used to wear `netIcon`, and that was wrong twice over. Critic, measuring
 * the left rail: *"the seventh (net) button is the only monochrome black icon in
 * a HUD where every other glyph is coloured, and its magnifier-with-mesh
 * silhouette is nearly identical to the zoom magnifiers in the opposite corner:
 * two magnifier-shaped things meaning entirely different things."* Both halves
 * are true — a hoop on a short handle at 45 degrees IS a magnifying glass, and
 * the net's mesh is drawn in `inkSoft` on `uiPanel` with no fill colour anywhere
 * in it.
 *
 * A closed book, standing, with a coloured cover and a ribbon. Nothing else in
 * the game is that shape, it is the thing the button actually opens, and the
 * colour is `uiAccent` — the HUD's own accent, so it belongs to the rail rather
 * than to the creatures. `netIcon` keeps its job as the BUGS PAGE TAB, where a
 * net is exactly right and there is no magnifier within a screen of it.
 */
export const bookButtonIcon: Painter = (c, size) =>
  inBox(c, size, () => {
    // The pages, offset behind the cover so the book has thickness.
    c.beginPath()
    c.moveTo(30, 16)
    c.lineTo(84, 16)
    c.lineTo(84, 86)
    c.lineTo(30, 86)
    c.closePath()
    fillStroke(c, 'uiPanel')
    // The cover.
    c.beginPath()
    c.moveTo(20, 12)
    c.lineTo(74, 12)
    c.lineTo(74, 90)
    c.lineTo(20, 90)
    c.closePath()
    fillStroke(c, 'uiAccent')
    // The spine, so it reads as a closed book and never as a card.
    c.beginPath()
    c.moveTo(31, 12)
    c.lineTo(31, 90)
    c.closePath()
    c.lineWidth = 6
    c.strokeStyle = INK
    c.stroke()
    // A ribbon marker hanging out of the bottom: the one asymmetric detail, and
    // what stops the silhouette being a plain rectangle at rail size.
    c.beginPath()
    c.moveTo(56, 12)
    c.lineTo(66, 12)
    c.lineTo(66, 62)
    c.lineTo(61, 54)
    c.lineTo(56, 62)
    c.closePath()
    fillStroke(c, 'flowerPink', 5)
    // Two little finds on the cover, in the book's own two page colours.
    dot(c, 44, 30, 5, 'flowerYellow')
    dot(c, 44, 76, 5, 'monsterLime')
  })

/** The book itself — the panel's own picture. */
export const bookIcon: Painter = (c, size) =>
  inBox(c, size, () => {
    c.beginPath()
    c.moveTo(50, 26)
    c.quadraticCurveTo(30, 12, 8, 20)
    c.lineTo(8, 82)
    c.quadraticCurveTo(30, 74, 50, 86)
    c.closePath()
    fillStroke(c, 'uiPanelAlt')
    c.beginPath()
    c.moveTo(50, 26)
    c.quadraticCurveTo(70, 12, 92, 20)
    c.lineTo(92, 82)
    c.quadraticCurveTo(70, 74, 50, 86)
    c.closePath()
    fillStroke(c, 'uiPanel')
    line(
      c,
      [
        [50, 26],
        [50, 86],
      ],
      6,
    )
    dot(c, 30, 46, 6, 'uiAccent')
    dot(c, 70, 46, 6, 'flowerPink')
    dot(c, 30, 62, 6, 'flowerYellow')
    dot(c, 70, 62, 6, 'monsterLime')
  })

/**
 * The other two pages belong to the cooking and zoo crews, and they need to be
 * TOLD APART AT A GLANCE. Both tabs were the same open book to begin with,
 * which in a captured frame was two identical white squares next to each other
 * — the exact thing a pre-reader cannot navigate. A pot and a paw.
 */
export const potIcon: Painter = (c, size) =>
  inBox(c, size, () => {
    c.beginPath()
    c.moveTo(16, 44)
    c.lineTo(84, 44)
    c.lineTo(76, 86)
    c.lineTo(24, 86)
    c.closePath()
    fillStroke(c, 'metal')
    line(
      c,
      [
        [10, 56],
        [16, 56],
      ],
      8,
    )
    line(
      c,
      [
        [84, 56],
        [90, 56],
      ],
      8,
    )
    c.beginPath()
    c.moveTo(10, 40)
    c.lineTo(90, 40)
    c.lineTo(90, 50)
    c.lineTo(10, 50)
    c.closePath()
    fillStroke(c, 'roofRed', 5)
    // steam
    for (const x of [34, 50, 66]) {
      c.beginPath()
      c.moveTo(x, 34)
      c.quadraticCurveTo(x + 8, 24, x, 12)
      c.lineWidth = 5
      c.strokeStyle = css('inkSoft')
      c.stroke()
    }
  })

export const pawIcon: Painter = (c, size) =>
  inBox(c, size, () => {
    ellipse(c, 50, 66, 27, 23)
    fillStroke(c, 'monsterCoral')
    dot(c, 26, 38, 11, 'monsterCoral')
    dot(c, 42, 26, 11, 'monsterCoral')
    dot(c, 60, 26, 11, 'monsterCoral')
    dot(c, 76, 38, 11, 'monsterCoral')
    for (const [x, y] of [
      [26, 38],
      [42, 26],
      [60, 26],
      [76, 38],
    ] as const) {
      ellipse(c, x, y, 11, 11)
      c.lineWidth = 5
      c.strokeStyle = INK
      c.stroke()
    }
  })

/** The tab for the fish page. A rod is clearer than "water" to a pre-reader. */
export const rodIcon: Painter = (c, size) =>
  inBox(c, size, () => {
    line(
      c,
      [
        [10, 88],
        [72, 16],
      ],
      9,
    )
    line(
      c,
      [
        [72, 16],
        [80, 46],
      ],
      3,
    )
    ellipse(c, 80, 54, 10, 10)
    fillStroke(c, 'flowerRed', 4)
    c.beginPath()
    c.moveTo(70, 54)
    c.lineTo(90, 54)
    c.lineWidth = 4
    c.strokeStyle = INK
    c.stroke()
    ellipse(c, 34, 74, 12, 9, -0.7)
    fillStroke(c, 'uiCoin', 4)
  })

/**
 * ===========================================================================
 *  "NOT FOUND YET" IS A PICTURE OF THE THING, NOT A PUNCTUATION MARK.
 * ===========================================================================
 * Round `vr2`: "half the page is filled with a drawn question mark — textContent
 * is empty so LAW #4 is technically intact, but '?' is a punctuation glyph, not
 * a picture, and it is the single most repeated element on the page. A
 * silhouette of the creature, or an empty net, says the same thing without
 * asking a pre-reader to know what '?' means."
 *
 * That is right, and the letter of the law was doing the work the spirit of it
 * was supposed to. A '?' is a grapheme: a three-year-old who cannot read cannot
 * read it either, and ten of them on one page is a page of writing.
 *
 * So an unfound creature now wears ITS OWN SHAPE, flattened to one soft ink
 * tone — the same picture the card will show once it is caught, with the colour
 * taken out. It says "there is something here and you have not got it" in the
 * one vocabulary this book has, and it is a real clue rather than a riddle: a
 * kid can see it is a butterfly before they have ever met one.
 *
 * `silhouette` composites through `source-in`, which repaints every pixel the
 * painter drew — fills, ink lines and eyes alike — in one flat colour, so the
 * result cannot leak the creature's actual palette however the painter is
 * written. It renders at 2x into an offscreen canvas and is cached per
 * (key, size), because `paint()` rebuilds every card on every repaint and a
 * fresh canvas per unfound card per repaint is real work on the old iPad.
 */
const silhouetteCache = new Map<string, HTMLCanvasElement>()

export function silhouette(key: string, p: Painter): Painter {
  return (c, size) => {
    const px = Math.max(24, Math.round(size))
    const cacheKey = `${key}:${px}`
    let off = silhouetteCache.get(cacheKey)
    if (off === undefined) {
      off = document.createElement('canvas')
      off.width = px * 2
      off.height = px * 2
      const oc = off.getContext('2d')
      if (oc === null) return
      oc.scale(2, 2)
      p(oc, px)
      oc.globalCompositeOperation = 'source-in'
      oc.fillStyle = css('inkSoft')
      oc.globalAlpha = 0.42
      oc.fillRect(0, 0, px, px)
      silhouetteCache.set(cacheKey, off)
    }
    c.drawImage(off, 0, 0, size, size)
  }
}

/**
 * The fallback for a card whose creature has no painter of its own — a recipe or
 * a zoo animal, which belong to other crews and may only carry an icon. An
 * EMPTY NET: the picture of "nothing in here yet", and the same net that is
 * already the bugs page's tab, so it is not a new symbol to learn.
 */
export const unknownIcon: Painter = (c, size) =>
  inBox(c, size, () => {
    line(
      c,
      [
        [20, 92],
        [46, 62],
      ],
      10,
    )
    // the hoop, seen face-on and EMPTY — the mesh sags, nothing is in it
    ellipse(c, 60, 42, 32, 30, -0.5)
    c.fillStyle = css('uiPanelAlt')
    c.fill()
    c.save()
    c.clip()
    c.strokeStyle = css('inkSoft')
    c.lineWidth = 3
    for (let i = -3; i <= 3; i++) {
      c.beginPath()
      c.moveTo(60 + i * 12, 4)
      c.lineTo(60 + i * 12, 80)
      c.stroke()
      c.beginPath()
      c.moveTo(22, 42 + i * 11)
      c.lineTo(98, 42 + i * 11)
      c.stroke()
    }
    c.restore()
    ellipse(c, 60, 42, 32, 30, -0.5)
    c.lineWidth = 7
    c.strokeStyle = css('inkSoft')
    c.stroke()
  })

// ---------------------------------------------------------------------------
// WHEN and WHERE — the two things a book page has to say without words
// ---------------------------------------------------------------------------

/**
 * The four bands of the day, as four pictures. A page shows all four and lights
 * the ones this creature is out in, so "fireflies come at night" is a thing a
 * pre-reader reads off the page rather than a thing they are told.
 */
export const phasePip: Readonly<Record<string, Painter>> = {
  dawn: (c, size) =>
    inBox(c, size, () => {
      line(
        c,
        [
          [8, 72],
          [92, 72],
        ],
        6,
      )
      c.beginPath()
      c.arc(50, 72, 26, Math.PI, 0)
      fillStroke(c, 'flowerPink')
      for (const a of [-2.6, -2.1, -1.6, -1.05, -0.55]) {
        line(
          c,
          [
            [50 + Math.cos(a) * 33, 72 + Math.sin(a) * 33],
            [50 + Math.cos(a) * 44, 72 + Math.sin(a) * 44],
          ],
          5,
        )
      }
    }),
  day: (c, size) =>
    inBox(c, size, () => {
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2
        line(
          c,
          [
            [50 + Math.cos(a) * 30, 50 + Math.sin(a) * 30],
            [50 + Math.cos(a) * 44, 50 + Math.sin(a) * 44],
          ],
          6,
        )
      }
      ellipse(c, 50, 50, 24, 24)
      fillStroke(c, 'uiCoin')
    }),
  dusk: (c, size) =>
    inBox(c, size, () => {
      line(
        c,
        [
          [8, 72],
          [92, 72],
        ],
        6,
      )
      c.beginPath()
      c.arc(50, 72, 26, Math.PI, 0)
      fillStroke(c, 'roofMustard')
      line(
        c,
        [
          [22, 84],
          [78, 84],
        ],
        5,
      )
    }),
  night: (c, size) =>
    inBox(c, size, () => {
      c.beginPath()
      c.arc(56, 50, 30, 0, Math.PI * 2)
      c.fillStyle = css('flowerWhite')
      c.fill()
      c.lineWidth = 6
      c.strokeStyle = INK
      c.stroke()
      c.save()
      c.beginPath()
      c.arc(56, 50, 33, 0, Math.PI * 2)
      c.clip()
      c.beginPath()
      c.arc(72, 40, 28, 0, Math.PI * 2)
      c.fillStyle = css('wallBlue')
      c.fill()
      c.lineWidth = 6
      c.strokeStyle = INK
      c.stroke()
      c.restore()
      dot(c, 16, 22, 5, 'uiCoin')
      dot(c, 30, 76, 4, 'uiCoin')
    }),
}

/** Where it lives. Four pictures, one per place tag this crew can produce. */
export const placePic: Readonly<Record<string, Painter>> = {
  grass: (c, size) =>
    inBox(c, size, () => {
      c.beginPath()
      c.moveTo(4, 86)
      c.quadraticCurveTo(50, 58, 96, 86)
      c.lineTo(96, 96)
      c.lineTo(4, 96)
      c.closePath()
      fillStroke(c, 'grass')
      for (const x of [22, 40, 58, 76]) {
        line(
          c,
          [
            [x, 78],
            [x - 6, 54],
          ],
          6,
        )
        line(
          c,
          [
            [x + 6, 78],
            [x + 12, 58],
          ],
          6,
        )
      }
    }),
  garden: (c, size) =>
    inBox(c, size, () => {
      c.beginPath()
      c.moveTo(4, 86)
      c.quadraticCurveTo(50, 66, 96, 86)
      c.lineTo(96, 96)
      c.lineTo(4, 96)
      c.closePath()
      fillStroke(c, 'grassDark')
      const petals: [number, number, PaletteName][] = [
        [26, 46, 'flowerPink'],
        [56, 34, 'flowerYellow'],
        [78, 52, 'flowerPurple'],
      ]
      for (const [x, y, col] of petals) {
        line(
          c,
          [
            [x, 82],
            [x, y + 12],
          ],
          6,
        )
        for (let i = 0; i < 5; i++) {
          const a = (i / 5) * Math.PI * 2
          dot(c, x + Math.cos(a) * 11, y + Math.sin(a) * 11, 8, col)
        }
        dot(c, x, y, 7, 'flowerWhite')
      }
    }),
  tree: (c, size) =>
    inBox(c, size, () => {
      line(
        c,
        [
          [50, 94],
          [50, 56],
        ],
        12,
      )
      ellipse(c, 34, 44, 24, 22)
      fillStroke(c, 'leaf')
      ellipse(c, 66, 44, 24, 22)
      fillStroke(c, 'leaf')
      ellipse(c, 50, 30, 26, 24)
      fillStroke(c, 'leafLight')
    }),
  pond: (c, size) =>
    inBox(c, size, () => {
      ellipse(c, 50, 60, 44, 30)
      fillStroke(c, 'water')
      c.save()
      c.beginPath()
      c.ellipse(50, 60, 44, 30, 0, 0, Math.PI * 2)
      c.clip()
      c.strokeStyle = css('waterDeep')
      c.lineWidth = 5
      for (const y of [48, 62, 76]) {
        c.beginPath()
        c.moveTo(10, y)
        c.quadraticCurveTo(30, y - 7, 50, y)
        c.quadraticCurveTo(70, y + 7, 92, y)
        c.stroke()
      }
      c.restore()
      ellipse(c, 50, 60, 44, 30)
      c.lineWidth = 6
      c.strokeStyle = INK
      c.stroke()
    }),
}

// ---------------------------------------------------------------------------
// mounting a painter in the DOM
// ---------------------------------------------------------------------------

/**
 * A painter as a HUD element. Deliberately shaped like `iconEl()` — same
 * `.mt-ico` class, same explicit inline size — because the HUD crew's
 * oversized-widget gate measures `.mt-ico` and an unsized picture is the exact
 * defect that gate exists to catch (see `measureOversized` in hud.ts).
 */
export function paintEl(p: Painter, sizePx: number, label = ''): HTMLSpanElement {
  const span = document.createElement('span')
  span.className = 'mt-ico'
  span.style.width = `${sizePx}px`
  span.style.height = `${sizePx}px`
  span.style.display = 'block'
  const canvas = document.createElement('canvas')
  const dpr = Math.min(2, Math.max(1, window.devicePixelRatio || 1))
  canvas.width = Math.round(sizePx * dpr)
  canvas.height = Math.round(sizePx * dpr)
  canvas.style.width = `${sizePx}px`
  canvas.style.height = `${sizePx}px`
  canvas.style.display = 'block'
  if (label !== '') canvas.setAttribute('aria-label', label)
  const c = canvas.getContext('2d')
  if (c !== null) {
    c.scale(dpr, dpr)
    p(c, sizePx)
  }
  span.append(canvas)
  return span
}
